<script setup lang="ts">
import { useDialogPluginComponent } from 'quasar';
import { computed } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import type { ContextDto } from '@/services/types/api.types';
import { useCommunityStore, useConsultationsStore } from '@/stores';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import StandardInput from '@/components/StandardInput.vue';
import { useI18n } from 'vue-i18n';
import { Constants } from '@/constants';

const { t } = useI18n();

const communitySTore = useCommunityStore();

const props = defineProps<{
  createUserPermission: (email: string, role: number) => Promise<boolean>;
}>();

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const { handleSubmit, errors, meta } = useForm({
  validationSchema: yup.object<ContextDto>({
    email: yup
      .string()
      .matches(Constants.EmailRegex, t('validation.format', { field: 'email' }))
      .required(),
  }),
});

const dialogTitle = computed(() => t('consultation.dialogs.addEditorTitle'));

const onSubmit = handleSubmit(async (values) => {
  await wrapSavingOperation(
    async () => await props.createUserPermission(values.email, Constants.User.Role.EDITOR),
    () => onDialogOK(),
  );
});
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ dialogTitle }}</div>
      </q-card-section>
      <q-separator />
      <q-card-section>
        <standard-input name="email" :label="$t('community.admins.email')" />
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="$t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="$t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<style></style>
